import { Box, Typography } from "@mui/material";
import useColors from "api/hooks/useColors";
import TextButton from "components/buttons/TextButton";
import CustomTooltip from "components/CustomTooltip";
import FilterMenu from "components/filters/FilterMenu";
import SearchInput from "components/filters/SearchInput";
import StyledDataGrid from "components/StyledDataGrid";
import { useState } from "react";
import IntegrationImage from "scenes/stack/integrations/components/IntegrationImage";

const NonApplicableControlsWidget = ({ nonApplicableControls, isLoading }) => {
  const colors = useColors();
  const [appliedFilters, setAppliedFilters] = useState({
    toolFilter: {},
    searchString: "",
  });

  const handleClearFilters = () => {
    setAppliedFilters({
      toolFilter: {},
      searchString: "",
    });
  };

  const toolOptions = [
    ...new Set((nonApplicableControls || []).map((c) => c.tool)),
  ]
    .filter(Boolean)
    .map((tool) => ({
      name: tool,
      label: tool,
      displayName: tool,
    }));

  const filteredControls = (() => {
    if (!nonApplicableControls) return [];
    let result = [...nonApplicableControls];

    const selectedTools = Object.keys(appliedFilters.toolFilter || {}).filter(
      (key) => appliedFilters.toolFilter[key]
    );
    if (selectedTools.length > 0) {
      result = result.filter((c) => selectedTools.includes(c.tool));
    }

    if (appliedFilters.searchString) {
      const search = appliedFilters.searchString.toLowerCase();
      result = result.filter(
        (c) =>
          c.control_id.toLowerCase().includes(search) ||
          c.title?.toLowerCase().includes(search) ||
          c.tool?.toLowerCase().includes(search)
      );
    }

    return result.sort((a, b) => b.customer_count - a.customer_count);
  })();

  const columns = [
    { field: "control_id", headerName: "Control ID", flex: 0.5 },
    {
      field: "tool",
      headerName: "Tool",
      flex: 0.6,
      renderCell: (params) => {
        if (!params.value) return "-";
        return (
          <Box display="flex" alignItems="center" gap={1}>
            <IntegrationImage
              name={params.value.toLowerCase().replace(/ /g, "_")}
              width="20px"
              showToolTip={true}
              newLogo={true}
            />
            <Typography variant="body2" sx={{ fontSize: "13px" }}>
              {params.value}
            </Typography>
          </Box>
        );
      },
    },
    { field: "title", headerName: "Title", flex: 2 },
    {
      field: "customer_count",
      headerName: "# Missing",
      flex: 0.5,
      renderCell: (params) => (
        <Typography
          variant="body2"
          sx={{ fontSize: "13px", color: colors.warning }}
        >
          {params.value}
        </Typography>
      ),
    },
    {
      field: "customers",
      headerName: "Customers",
      flex: 2,
      renderCell: (params) => {
        const customers = params.value || [];
        const customerIds = customers.map((c) => c.customer_id);
        const displayIds = customerIds.slice(0, 5);
        const remaining = customerIds.length - 5;

        return (
          <Box display="flex" alignItems="center" gap={0.5}>
            <Typography variant="body2" sx={{ fontSize: "13px" }}>
              {displayIds.join(", ")}
            </Typography>
            {remaining > 0 && (
              <CustomTooltip title={customerIds.slice(5).join(", ")}>
                <Typography variant="caption" color="text.secondary">
                  +{remaining} more
                </Typography>
              </CustomTooltip>
            )}
          </Box>
        );
      },
    },
  ];

  return (
    <Box
      sx={{ height: "100%", display: "flex", flexDirection: "column", gap: 2 }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          gap: 1.5,
          alignItems: "center",
          padding: "8px 0",
          borderBottom: `1px solid ${colors.divider2}`,
        }}
      >
        <Box sx={{ minWidth: "160px" }}>
          <FilterMenu
            filterName="Tool"
            fieldName="toolFilter"
            options={toolOptions}
            appliedFilters={appliedFilters}
            setAppliedFilters={setAppliedFilters}
            showSearch={true}
          />
        </Box>
        <Box sx={{ flex: 1 }}>
          <SearchInput
            fieldName="searchString"
            appliedFilters={appliedFilters}
            setAppliedFilters={setAppliedFilters}
            placeholder="Search controls..."
          />
        </Box>
        <Box>
          <TextButton onClick={handleClearFilters} size="small">
            Clear
          </TextButton>
        </Box>
      </Box>

      <Box sx={{ flex: 1, minHeight: 0 }}>
        <StyledDataGrid
          rows={filteredControls.map((control, idx) => ({
            id: idx,
            ...control,
          }))}
          columns={columns}
          loading={isLoading}
          disableRowSelectionOnClick
          pageSizeOptionsOverride={[10, 25, 50]}
          autoHeight={false}
          boxSXProps={{ height: "100%" }}
        />
      </Box>
    </Box>
  );
};

export default NonApplicableControlsWidget;
